// Helpers to resolve a user's compensation from dated user_contract periods.
//
// user_contract is the source of truth for pay terms; user.* fields are a
// denormalized snapshot of the period active *today*. Month-specific reports
// must resolve the period active on the relevant month, not read the snapshot.
//
// The period active on a date = the user's period with the greatest
// start_date <= date. Every resolver falls back to the user snapshot so a user
// without periods (or a period missing a computed field) still yields a value.

import api from "../services/api";

// Fetch user_contract periods, optionally scoped to a business unit.
export async function fetchUserContracts({ businessUnitId } = {}) {
  const body = { limit: 5000 };
  if (businessUnitId) body.business_unit_id = businessUnitId;
  const { ok, data } = await api.post("/user_contract/search", body);
  return ok ? data || [] : [];
}

// Group periods by user_id, each list sorted by start_date descending (newest first).
export function groupPeriodsByUser(contracts) {
  const byUser = {};
  for (const c of contracts || []) {
    if (!c.user_id || !c.start_date) continue;
    (byUser[c.user_id] || (byUser[c.user_id] = [])).push(c);
  }
  for (const id in byUser) byUser[id].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  return byUser;
}

// The period active on `date` for a user (greatest start_date <= date), or null.
export function getActivePeriod(periodsByUser, userId, date) {
  const list = periodsByUser?.[userId];
  if (!list || !list.length) return null;
  const d = date instanceof Date ? date : new Date(date);
  return list.find((p) => new Date(p.start_date) <= d) || null;
}

// Monthly cost for a user on a given date: active period's total_monthly_cost, snapshot fallback.
// A period cost of 0 also falls back to the snapshot: in practice 0 means the field was left
// empty when the period was (re)saved, not that the person works for free — a 0-cost period
// silently zeroed employees out of the P&L twice (Justi, Aug 2026).
export function resolveMonthlyCost(periodsByUser, user, date) {
  const period = getActivePeriod(periodsByUser, user._id, date);
  return period?.total_monthly_cost || user.total_monthly_cost || 0;
}

// Daily cost (TJM) for a user on a given date, snapshot fallback.
export function resolveTjm(periodsByUser, user, date) {
  const period = getActivePeriod(periodsByUser, user._id, date);
  return (period?.tjm ?? user.tjm) || 0;
}

// Contract type active on a date, snapshot fallback.
export function resolveContractType(periodsByUser, user, date) {
  const period = getActivePeriod(periodsByUser, user._id, date);
  return period?.contract_type || user.contract || "";
}
